/**
 * useAppUser Hook
 * Merges Hiyve Cloud identity (from IdentityProvider) with server-side
 * UserProfile metadata. Provides the same user.metadata.* shape that
 * the old muzieauth-react useAuth() returned.
 */

import { useUser } from '@hiyve/react-identity';
import { useQuery } from '@tanstack/react-query';
import { useApi } from './useApi';

export function useAppUser() {
  const { user: identityUser, isAuthenticated, isLoading: authLoading } = useUser();
  const api = useApi();

  // Fetch server-side profile metadata (apiKey, roles, org, etc.)
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['user-profile', identityUser?.email],
    queryFn: () => api.get('/api/organizations/onboarding-status'),
    enabled: isAuthenticated && !!identityUser?.email,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });

  // Build a user object that matches the old metadata shape
  const user = identityUser ? {
    id: identityUser.id,
    email: identityUser.email,
    name: identityUser.name,
    emailVerified: identityUser.emailVerified,
    picture: profile?.picture,
    metadata: {
      apiKey: profile?.apiKey,
      organization: profile?.organization,
      brandName: profile?.brandName,
      brandOrgId: profile?.brandOrgId,
      tfaEnabled: profile?.tfaEnabled, // per-tenant 2FA setting (null when unknown)
      identityBinding: profile?.identityBinding, // per-tenant IF-02 policy: off|warn|enforce (null when unknown)
      roles: profile?.roles || [],
      phone: profile?.phone,
      active: profile?.active,
      stripeCustomerId: profile?.stripeCustomerId,
      isAdmin: profile?.roles?.includes('admin') || false,
      // Gates the tenant user-management page. Visibility only — the real
      // control is the same role check in server/src/routes/tenantUsers.js.
      isUserManager: profile?.roles?.includes('user-manager') || false,
      isOwner: profile?.roles?.includes('org-owner') || false,
      isDev: profile?.roles?.includes('org-developer') || false,
      isOrgAdmin: profile?.roles?.includes('org-administrator') || false,
    },
  } : null;

  return {
    user,
    isAuthenticated,
    isLoading: authLoading || (isAuthenticated && profileLoading),
    profile,
  };
}

export default useAppUser;
